import api from './config';

/**
 * API Client para Módulo de Historial de Personal
 */
const employeeHistoryAPI = {

  // ═══════════════════════════════════════════════════════════════
  // OBTENER HISTORIAL
  // ═══════════════════════════════════════════════════════════════

  /**
   * Obtener historial de toda la empresa con filtros
   */
  async getCompanyHistory(filters = {}) {
    try {
      const params = new URLSearchParams();

      if (filters.employee_id) params.append('employee_id', filters.employee_id);
      if (filters.tipo_evento) params.append('tipo_evento', filters.tipo_evento);
      if (filters.categoria) params.append('categoria', filters.categoria);
      if (filters.fecha_desde) params.append('fecha_desde', filters.fecha_desde);
      if (filters.fecha_hasta) params.append('fecha_hasta', filters.fecha_hasta);
      if (filters.activo !== undefined) params.append('activo', filters.activo);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.offset) params.append('offset', filters.offset);

      const queryString = params.toString();
      const url = `/employee-history/company${queryString ? '?' + queryString : ''}`;

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error al obtener historial de la empresa:', error);
      throw error;
    }
  },

  /**
   * Obtener historial completo de un empleado con filtros
   */
  async getEmployeeHistory(employeeId, filters = {}) {
    try {
      const params = new URLSearchParams();

      if (filters.tipo_evento) params.append('tipo_evento', filters.tipo_evento);
      if (filters.categoria) params.append('categoria', filters.categoria);
      if (filters.fecha_desde) params.append('fecha_desde', filters.fecha_desde);
      if (filters.fecha_hasta) params.append('fecha_hasta', filters.fecha_hasta);
      if (filters.activo !== undefined) params.append('activo', filters.activo);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.offset) params.append('offset', filters.offset);

      const queryString = params.toString();
      const url = `/employee-history/employee/${employeeId}${queryString ? '?' + queryString : ''}`;

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error al obtener historial del empleado:', error);
      throw error;
    }
  },

  /**
   * Obtener timeline (vista cronológica agrupada)
   */
  async getTimeline(employeeId) {
    try {
      const response = await api.get(`/employee-history/employee/${employeeId}/timeline`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener timeline:', error);
      throw error;
    }
  },

  /**
   * Obtener un evento específico
   */
  async getEvent(eventId) {
    try {
      const response = await api.get(`/employee-history/${eventId}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener evento:', error);
      throw error;
    }
  },

  /**
   * Obtener estadísticas del historial de un empleado
   */
  async getStats(employeeId) {
    try {
      const response = await api.get(`/employee-history/employee/${employeeId}/stats`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener estadísticas:', error);
      throw error;
    }
  },

  // ═══════════════════════════════════════════════════════════════
  // CREAR Y MODIFICAR
  // ═══════════════════════════════════════════════════════════════

  /**
   * Crear nuevo evento en el historial
   */
  async createEvent(eventData) {
    try {
      const response = await api.post('/employee-history', eventData);
      return response.data;
    } catch (error) {
      console.error('Error al crear evento:', error);
      throw error;
    }
  },

  /**
   * Actualizar evento existente
   */
  async updateEvent(eventId, eventData) {
    try {
      const response = await api.put(`/employee-history/${eventId}`, eventData);
      return response.data;
    } catch (error) {
      console.error('Error al actualizar evento:', error);
      throw error;
    }
  },

  /**
   * Inactivar evento (no eliminar)
   */
  async inactivateEvent(eventId, motivo) {
    try {
      const response = await api.delete(`/employee-history/${eventId}`, {
        data: { motivo }
      });
      return response.data;
    } catch (error) {
      console.error('Error al inactivar evento:', error);
      throw error;
    }
  },

  /**
   * Reactivar evento inactivo
   */
  async reactivateEvent(eventId) {
    try {
      const response = await api.post(`/employee-history/${eventId}/reactivate`);
      return response.data;
    } catch (error) {
      console.error('Error al reactivar evento:', error);
      throw error;
    }
  },

  // ═══════════════════════════════════════════════════════════════
  // UTILIDADES
  // ═══════════════════════════════════════════════════════════════

  /**
   * Obtener filtros disponibles (categorías y tipos de eventos)
   */
  async getAvailableFilters() {
    try {
      const response = await api.get('/employee-history/filters/available');
      return response.data;
    } catch (error) {
      console.error('Error al obtener filtros:', error);
      throw error;
    }
  },

  // ═══════════════════════════════════════════════════════════════
  // HELPERS DE REGISTRO RÁPIDO
  // ═══════════════════════════════════════════════════════════════

  /**
   * Registrar ingreso de empleado
   */
  async registrarIngreso(employeeId, data) {
    return this.createEvent({
      employee_id: employeeId,
      tipo_evento: 'ingreso',
      categoria: 'contratacion',
      titulo: 'Ingreso a la empresa',
      descripcion: data.descripcion,
      fecha_efectiva: data.fecha_ingreso,
      metadata: data.metadata
    });
  },

  /**
   * Registrar promoción
   */
  async registrarPromocion(employeeId, data) {
    return this.createEvent({
      employee_id: employeeId,
      tipo_evento: 'promocion',
      categoria: 'organizacional',
      titulo: data.titulo || 'Promoción',
      descripcion: data.descripcion,
      valor_anterior: { posicion: data.posicion_anterior },
      valor_nuevo: { posicion: data.posicion_nueva },
      fecha_efectiva: data.fecha_efectiva,
      metadata: data.metadata
    });
  },

  /**
   * Registrar aumento salarial
   */
  async registrarAumentoSalarial(employeeId, data) {
    return this.createEvent({
      employee_id: employeeId,
      tipo_evento: 'aumento_salarial',
      categoria: 'salarial',
      titulo: data.titulo || 'Aumento Salarial',
      descripcion: data.descripcion,
      valor_anterior: { salario: data.salario_anterior },
      valor_nuevo: { salario: data.salario_nuevo },
      fecha_efectiva: data.fecha_efectiva,
      metadata: data.metadata
    });
  },

  /**
   * Registrar cambio de departamento
   */
  async registrarCambioDepartamento(employeeId, data) {
    return this.createEvent({
      employee_id: employeeId,
      tipo_evento: 'cambio_departamento',
      categoria: 'organizacional',
      titulo: 'Cambio de Departamento',
      descripcion: data.descripcion,
      valor_anterior: { departamento: data.departamento_anterior },
      valor_nuevo: { departamento: data.departamento_nuevo },
      fecha_efectiva: data.fecha_efectiva,
      metadata: data.metadata
    });
  },

  /**
   * Registrar acción disciplinaria
   */
  async registrarAccionDisciplinaria(employeeId, data) {
    return this.createEvent({
      employee_id: employeeId,
      tipo_evento: data.tipo, // amonestacion_verbal, amonestacion_escrita, suspension
      categoria: 'disciplinario',
      titulo: data.titulo,
      descripcion: data.descripcion,
      fecha_efectiva: data.fecha_efectiva,
      documentos_adjuntos: data.documentos,
      metadata: data.metadata
    });
  },

  /**
   * Registrar reconocimiento
   */
  async registrarReconocimiento(employeeId, data) {
    return this.createEvent({
      employee_id: employeeId,
      tipo_evento: data.tipo || 'reconocimiento',
      categoria: 'reconocimiento',
      titulo: data.titulo,
      descripcion: data.descripcion,
      fecha_efectiva: data.fecha_efectiva,
      metadata: data.metadata
    });
  },

  /**
   * Registrar capacitación
   */
  async registrarCapacitacion(employeeId, data) {
    return this.createEvent({
      employee_id: employeeId,
      tipo_evento: data.tipo || 'capacitacion_interna',
      categoria: 'capacitacion',
      titulo: data.titulo,
      descripcion: data.descripcion,
      fecha_efectiva: data.fecha_efectiva,
      metadata: {
        duracion: data.duracion,
        institucion: data.institucion,
        certificado: data.certificado,
        ...data.metadata
      }
    });
  },

  /**
   * Registrar terminación de contrato
   */
  async registrarTerminacion(employeeId, data) {
    return this.createEvent({
      employee_id: employeeId,
      tipo_evento: data.tipo, // renuncia, despido, terminacion_contrato, retiro
      categoria: 'terminacion',
      titulo: data.titulo,
      descripcion: data.motivo,
      fecha_efectiva: data.fecha_terminacion,
      documentos_adjuntos: data.documentos,
      metadata: data.metadata
    });
  }

};

export default employeeHistoryAPI;
